/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filterregel-functionalias-edit.component.ts
 * Gegenereerd met Junie (AI), 01-12-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Editorcomponent voor functionalias-regels met multiselect op functionalias inclusief vrije invoer.
 */

import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {FilterregelEditComponent} from './filterregel-edit.component';
import {FiltertoolPlanitemfunctiealiasRegel} from '../../../services/filtertool/regels/filtertool-functiealiasregel';
import {FiltertoolRegelTypeNamen} from '../../../services/filtertool/filtertool-regels';
import {FilterregelActiesComponent} from './filterregel-acties.component';
import {Planitem} from '../../../models/planitem';
import {DateTime} from 'luxon';

/**
 * Editor voor functiealiasregels: selecteer 1 of meer functionalias-waarden (multiselect) met vrije invoer en beheer verwijderen.
 */
@Component({
    standalone: true,
    selector: 'filterregel-functionalias-edit',
    imports: [CommonModule, FormsModule, NgSelectModule, FilterregelActiesComponent],
    templateUrl: './filterregel-functionalias-edit.component.html',
    styleUrls: ['./filterregel-functionalias-edit.component.scss', './filterregel-editors.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterregelFunctiealiasEditComponent extends FilterregelEditComponent<FiltertoolPlanitemfunctiealiasRegel> implements OnInit {
    // Inputs (properties)
    /** De te bewerken functiealiasregel. */
    @Input({required: true}) override regel!: FiltertoolPlanitemfunctiealiasRegel;

    // Properties
    /** Mapping met label/omschrijving per regeltype voor weergave. */
    public readonly regelTypeNamen = FiltertoolRegelTypeNamen;
    /** Beschikbare functionalias-waarden uit het afgelopen jaar. */
    public beschikbareFunctiealiassen: string[] = [];
    /** Lokale binding van gekozen waarden voor ngModel. */
    public gekozenFunctiealiasWaarden: string[] = [];

    // Constructor
    constructor() {
        super('debug');
    }

    // Lifecycle hooks
    async ngOnInit() {
        // init selectie
        this.gekozenFunctiealiasWaarden = Array.isArray(this.regel?.functiealiasWaarden) ? [...this.regel.functiealiasWaarden] : [];
        
        // haal unieke functionalias waarden op uit planitems van het afgelopen jaar
        const eenJaarGeleden = DateTime.now().minus({ years: 1 }).toISODate();
        const planitems = await Planitem.getAll([], [[Planitem, {
            where: {
                startdate: {
                    $gte: eenJaarGeleden
                }
            },
            attributes: ['functionalias']
        }]]);
        
        // filter unieke niet-null waarden
        const aliassen = planitems
            .map(pi => pi.functionalias)
            .filter(alias => alias != null && alias !== '');
        this.beschikbareFunctiealiassen = [...new Set(aliassen)].sort();
        
        // trigger hertekenen omdat OnPush + @if anders niet update na async load
        this.cd.markForCheck();
        this.log.debug(`Beschikbare functiealiassen`, this.beschikbareFunctiealiassen);
        this.log.debug(`Regel`, this.regel);
        this.log.debug(`Gekozen`, this.gekozenFunctiealiasWaarden);
        this.log.debug(`is regel geldig?`, this.isRegelGeldig);
    }

    // Public methods
    /**
     * Slaat gewijzigde selectie op in de regel en emit de wijziging.
     */
    public functiealiassenGewijzigd(): void {
        // cleanup: verwijder null/undefined/lege strings
        const clean = (this.gekozenFunctiealiasWaarden || []).filter(v => v != null && v !== '');
        // schrijf direct in de regel
        if (this.regel) {
            // immutabele toekenning zodat getters direct goed reageren
            this.regel.functiealiasWaarden = [...clean];
            // emit optioneel wijzig-event voor dirty-state
            this.wijzig.emit(this.regel);
        }
        // OnPush: forceer directe CD (ng-select kan buiten Angular zone emiteren)
        try {
            this.cd.detectChanges();
        } catch {
            this.cd.markForCheck();
        }
        this.log.debug(`is regel geldig?`, this.isRegelGeldig);
    }
}
